/**
 * Message Input - Slack-like input area
 */

import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Upload, Tooltip, message as antMessage, Modal, Avatar, Typography, Popover } from 'antd';
import { SendOutlined, PaperClipOutlined, SmileOutlined, UserOutlined, CloseOutlined } from '@ant-design/icons';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import type { UploadFile } from 'antd/es/upload/interface';
import { CreateTicketFromChatModal } from './CreateTicketFromChatModal';
import './MessageInput.css';

const { TextArea } = Input;

interface MessageInputProps {
  channelId: string;
  replyTo?: string | null;
  onCancelReply?: () => void;
  onMessageSent?: () => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({ channelId, replyTo, onCancelReply, onMessageSent }) => {
  const { sendMessage, startTyping, stopTyping, isConnected } = useChatWebSocket();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [userToInvite, setUserToInvite] = useState<any>(null);
  const [pendingMessage, setPendingMessage] = useState('');
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [slashCommandOpen, setSlashCommandOpen] = useState(false);
  const [createTicketModalOpen, setCreateTicketModalOpen] = useState(false);
  const textareaRef = useRef<any>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const SLASH_COMMANDS = [
    { command: '/createticket', label: 'Create a new support ticket', action: () => { setSlashCommandOpen(false); setText(''); setCreateTicketModalOpen(true); } },
  ];

  const EMOJI_LIST = [
    '😊', '😄', '😃', '😁', '😆', '😂', '🤣', '😉', '😍', '🥰', '😘', '👍', '👎', '👌', '✌️', '🤞',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💔', '❣️', '💕', '🎉', '🎊', '🎈', '🎁', '📧', '📝',
    '💻', '📱', '🔔', '✅', '❌', '⚠️', '🔴', '🟢', '🟡', '⭐', '🔥', '✨', '🙏', '👋', '🤝', '💼',
  ];

  const insertEmoji = (emoji: string) => {
    const ta = textareaRef.current?.resizableTextArea?.textArea;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const before = text.substring(0, start);
      const after = text.substring(end);
      setText(before + emoji + after);
      setEmojiPickerOpen(false);
      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setText(prev => prev + emoji);
      setEmojiPickerOpen(false);
    }
  };

  // Fetch channel members
  const { data: membersData } = useQuery(
    ['channel-members', channelId],
    () => api.get(`/api/chat/channels/${channelId}/members`).then(res => res.data),
    {
      enabled: !!channelId,
    }
  );

  // Fetch replying-to message details
  const { data: replyMessageData } = useQuery(
    ['reply-message', channelId, replyTo],
    () => api.get(`/api/chat/channels/${channelId}/messages`).then(res => {
      const message = res.data.messages?.find((m: any) => m.id === replyTo);
      return message;
    }),
    {
      enabled: !!replyTo && !!channelId,
    }
  );

  // Fetch all users for mentions
  const { data: allUsersData } = useQuery(
    'chat-all-users',
    () => api.get('/api/chat/users').then(res => res.data),
  );

  const members = membersData?.members || [];
  const allUsers = allUsersData?.users || [];

  // Handle typing indicators
  useEffect(() => {
    if (text.length > 0) {
      startTyping(channelId);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping(channelId);
      }, 2000);
    } else {
      stopTyping(channelId);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [text, channelId, startTyping, stopTyping]);

  // Handle @ mention and / slash command detection
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    setText(value);
    setCursorPosition(cursorPos);

    const textBeforeCursor = value.substring(0, cursorPos);
    const lineStart = value.lastIndexOf('\n', cursorPos - 1) + 1;
    const lineText = value.substring(lineStart, cursorPos);

    // Slash command: show when user types / at start of line
    if (lineText.startsWith('/')) {
      setSlashCommandOpen(true);
      // If they type /createticket exactly and press Enter, we handle in handleKeyPress
    } else {
      setSlashCommandOpen(false);
    }

    // Check for @ mentions
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentionDropdown(true);
    } else {
      setShowMentionDropdown(false);
      setMentionQuery('');
    }
  };

  // Get filtered users for mentions
  const getMentionableUsers = () => {
    const query = mentionQuery.toLowerCase();
    return allUsers.filter((user: any) => {
      const firstName = (user.first_name || user.firstName || '').toLowerCase();
      const lastName = (user.last_name || user.lastName || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName.includes(query) || firstName.startsWith(query) || lastName.startsWith(query) || email.includes(query);
    }).slice(0, 10); // Limit to 10 results
  };

  // Handle mention selection
  const handleMentionSelect = (user: any) => {
    const textBeforeCursor = text.substring(0, cursorPosition);
    const textAfterCursor = text.substring(cursorPosition);
    const beforeMention = textBeforeCursor.replace(/@\w*$/, '');
    
    const firstName = user.first_name || user.firstName || '';
    const lastName = user.last_name || user.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const mentionText = fullName || user.email;
    
    const newText = `${beforeMention}@${mentionText} ${textAfterCursor}`;
    setText(newText);
    setShowMentionDropdown(false);
    setMentionQuery('');
    
    // Check if user is in channel
    const isMember = members.some((m: any) => m.id === user.id);
    if (!isMember && channelId) {
      setUserToInvite(user);
      setPendingMessage(newText);
      setInviteModalVisible(true);
    }
  };

  // Handle invite and send message
  const handleInviteAndSend = async () => {
    if (!userToInvite) return;
    
    try {
      // Invite user to channel
      await api.post(`/api/chat/channels/${channelId}/members`, {
        userIds: [userToInvite.id],
      });
      antMessage.success(`Invited ${userToInvite.first_name || userToInvite.firstName || userToInvite.email} to the channel`);
      queryClient.invalidateQueries(['channel-members', channelId]);
      
      // Send the message
      setText(pendingMessage);
      setInviteModalVisible(false);
      setUserToInvite(null);
      setPendingMessage('');
      
      // Trigger send
      setTimeout(() => {
        handleSend();
      }, 100);
    } catch (error: any) {
      antMessage.error(error.response?.data?.error || 'Failed to invite user');
    }
  };

  // Handle send without invite
  const handleSendWithoutInvite = () => {
    setText(pendingMessage);
    setInviteModalVisible(false);
    setUserToInvite(null);
    setPendingMessage('');
    
    setTimeout(() => {
      handleSend();
    }, 100);
  };

  const handleSend = async () => {
    if ((!text.trim() && fileList.length === 0) || sending || uploading || !isConnected) return;

    setSending(true);
    try {
      // Upload files first if any
      let attachmentUrl: string | undefined = undefined;
      let attachmentName: string | undefined = undefined;
      let attachmentSize: number | undefined = undefined;
      
      if (fileList.length > 0) {
        setUploading(true);
        const formData = new FormData();
        const fileItem = fileList[0];
        // Try multiple ways to get the File object
        const file = (fileItem as any).file || fileItem.originFileObj || fileItem;
        
        console.log('[MessageInput] Attempting upload:', {
          fileListLength: fileList.length,
          fileItem,
          hasFile: !!(fileItem as any).file,
          hasOriginFileObj: !!fileItem.originFileObj,
          fileType: file instanceof File ? 'File' : typeof file,
          fileName: file?.name,
          fileSize: file?.size
        });
        
        // Ensure we have a File object
        if (!file || !(file instanceof File)) {
          console.error('[MessageInput] Invalid file object:', file);
          antMessage.error('Invalid file selected. Please try selecting the file again.');
          setSending(false);
          setUploading(false);
          setFileList([]);
          return;
        }
        
        formData.append('file', file);
        
        try {
          console.log('[MessageInput] Uploading file:', file.name, file.size, file.type);
          const uploadResponse = await api.post('/api/chat/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log('[MessageInput] Upload successful:', uploadResponse.data);
          attachmentUrl = uploadResponse.data.url;
          attachmentName = uploadResponse.data.name || file.name;
          attachmentSize = uploadResponse.data.size || file.size || 0;
        } catch (uploadError: any) {
          console.error('[MessageInput] Upload error:', uploadError);
          const errorMessage = uploadError?.response?.data?.error || uploadError?.message || 'Failed to upload file';
          antMessage.error(errorMessage);
          setSending(false);
          setUploading(false);
          setFileList([]);
          return;
        }
        setUploading(false);
      }

      // Ensure text is sent as empty string if there's an attachment, or omit if no text and no attachment
      const messageText = text.trim() || '';
      await sendMessage(channelId, messageText, replyTo || undefined, attachmentUrl, attachmentName, attachmentSize);
      queryClient.invalidateQueries(['chat-messages', channelId]);
      setText('');
      setFileList([]);
      if (onCancelReply) {
        onCancelReply();
      }
      if (onMessageSent) {
        onMessageSent();
      }
      stopTyping(channelId);
      
      // Scroll to bottom after sending message - use multiple methods for reliability
      setTimeout(() => {
        // Method 1: Use exposed scroll function
        const scrollFn = (window as any).scrollChatToBottom;
        if (scrollFn) {
          scrollFn();
        }
        // Method 2: Direct DOM manipulation
        const container = (window as any).chatMessageListContainer || document.querySelector('.message-list-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 300);
    } catch (error) {
      console.error('Error sending message:', error);
      antMessage.error('Failed to send message');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleFileChange = (info: any) => {
    const { file } = info;
    console.log('[MessageInput] File change:', {
      status: file.status,
      name: file.name,
      hasOriginFileObj: !!file.originFileObj,
      file: file
    });
    
    // Handle file removal
    if (file.status === 'removed') {
      setFileList([]);
      return;
    }
    
    // Handle errors
    if (file.status === 'error') {
      antMessage.error('File upload failed');
      setFileList([]);
      return;
    }
    
    // Capture the file - prioritize originFileObj, fallback to file itself
    const fileObj = file.originFileObj || file;
    
    if (fileObj && (fileObj instanceof File || fileObj.name)) {
      const fileItem: UploadFile = {
        uid: file.uid || `file-${Date.now()}`,
        name: fileObj.name || file.name,
        status: 'done',
        originFileObj: fileObj instanceof File ? (fileObj as any) : undefined
      };
      
      // Store the actual File object for later upload
      if (fileObj instanceof File) {
        (fileItem as any).file = fileObj;
      }
      
      console.log('[MessageInput] Storing file:', fileItem);
      setFileList([fileItem]);
    }
  };

  const beforeUpload = (file: File) => {
    const isValidType = file.type.startsWith('image/') || 
                       file.type === 'application/pdf' || 
                       file.type.startsWith('text/') ||
                       file.type === 'application/msword' ||
                       file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const isValidSize = file.size <= 25 * 1024 * 1024; // 25MB
    
    if (!isValidType) {
      antMessage.error('Only images, PDFs, and text files are allowed');
      return false; // Prevent auto-upload but allow file to be added
    }
    if (!isValidSize) {
      antMessage.error('File size must be less than 25MB');
      return false; // Prevent auto-upload but allow file to be added
    }
    // Return false to prevent auto-upload, but allow file to be added to list
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setSlashCommandOpen(false);
      setShowMentionDropdown(false);
      setEmojiPickerOpen(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const lineStart = text.lastIndexOf('\n', cursorPosition - 1) + 1;
      const lineText = text.substring(lineStart, cursorPosition).trim();
      if (lineText === '/createticket') {
        e.preventDefault();
        setSlashCommandOpen(false);
        setText('');
        setCreateTicketModalOpen(true);
        return;
      }
      e.preventDefault();
      handleSend();
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="message-input">
      {replyTo && replyMessageData && (
        <div className="message-input-reply-preview">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0f0f0', borderRadius: '4px 4px 0 0' }}>
            <div style={{ width: 2, height: 32, background: '#1d74f5', borderRadius: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography.Text strong style={{ fontSize: 12 }}>
                {(replyMessageData.user?.firstName ?? replyMessageData.user?.first_name)} {(replyMessageData.user?.lastName ?? replyMessageData.user?.last_name)}
              </Typography.Text>
              <Typography.Text 
                ellipsis 
                style={{ display: 'block', fontSize: 12, color: '#666', marginTop: 2 }}
              >
                {(replyMessageData.text ?? replyMessageData.content) || 'Attachment'}
              </Typography.Text>
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={onCancelReply}
              style={{ padding: 0, width: 20, height: 20 }}
            />
          </div>
        </div>
      )}
      {fileList.length > 0 && (
        <div className="message-input-files">
          {fileList.map((file, index) => {
            const fileObj = file.originFileObj || (file as any).file;
            const isImage = fileObj && fileObj.type && fileObj.type.startsWith('image/');
            const previewUrl = isImage && fileObj ? URL.createObjectURL(fileObj) : null;
            
            return (
              <div key={index} className="message-file-preview">
                {isImage && previewUrl ? (
                  <div className="file-preview-image">
                    <img src={previewUrl} alt={file.name} />
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        URL.revokeObjectURL(previewUrl);
                        setFileList([]);
                      }}
                      className="file-preview-remove"
                    />
                  </div>
                ) : (
                  <div className="file-preview-document">
                    <div className="file-preview-icon">📎</div>
                    <div className="file-preview-info">
                      <div className="file-preview-name">{file.name}</div>
                      <div className="file-preview-size">
                        {fileObj && fileObj.size ? formatFileSize(fileObj.size) : ''}
                      </div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => setFileList([])}
                      className="file-preview-remove"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="message-input-wrapper">
        <div className="message-input-actions">
          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={beforeUpload}
            showUploadList={false}
            maxCount={1}
            accept="image/*,.pdf,.txt,.doc,.docx"
            customRequest={({ file, onSuccess, onError }) => {
              // Store file immediately when selected
              const fileObj = file as File;
              if (fileObj instanceof File) {
                // Validate file type and size
                const isValidType = fileObj.type.startsWith('image/') || 
                                 fileObj.type === 'application/pdf' || 
                                 fileObj.type.startsWith('text/') ||
                                 fileObj.type === 'application/msword' ||
                                 fileObj.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                const isValidSize = fileObj.size <= 25 * 1024 * 1024; // 25MB
                
                if (!isValidType) {
                  antMessage.error('Only images, PDFs, and text files are allowed');
                  onError?.(new Error('Invalid file type'));
                  return;
                }
                if (!isValidSize) {
                  antMessage.error('File size must be less than 25MB');
                  onError?.(new Error('File too large'));
                  return;
                }
                
                const fileItem: UploadFile = {
                  uid: `file-${Date.now()}`,
                  name: fileObj.name,
                  status: 'done',
                  originFileObj: fileObj as any
                };
                
                // Also store the file directly for easier access
                (fileItem as any).file = fileObj;
                
                console.log('[MessageInput] File selected via customRequest:', fileItem);
                setFileList([fileItem]);
                
                // Call onSuccess to prevent Ant Design from removing the file
                setTimeout(() => {
                  onSuccess?.('ok');
                }, 0);
              } else {
                onError?.(new Error('Invalid file'));
              }
            }}
          >
            <Tooltip title="Attach file">
              <Button 
                type="text" 
                icon={<PaperClipOutlined />} 
                className="message-input-action-btn"
                disabled={!isConnected || sending}
              />
            </Tooltip>
          </Upload>
          <Popover
            content={
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: 280, maxHeight: 200, overflowY: 'auto' }}>
                <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block', width: '100%' }}>Choose emoji</Typography.Text>
                {EMOJI_LIST.map((emoji) => (
                  <Button
                    key={emoji}
                    type="text"
                    size="small"
                    onClick={() => insertEmoji(emoji)}
                    style={{ padding: 4, fontSize: '1.2rem', minWidth: 32, height: 32 }}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            }
            title={null}
            trigger="click"
            open={emojiPickerOpen}
            onOpenChange={setEmojiPickerOpen}
          >
            <Tooltip title="Add emoji">
              <Button 
                type="text" 
                icon={<SmileOutlined />} 
                className="message-input-action-btn"
                disabled={!isConnected || sending}
                onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
              />
            </Tooltip>
          </Popover>
        </div>
        <div style={{ position: 'relative', flex: 1 }}>
          <TextArea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onKeyPress={handleKeyPress}
            placeholder={isConnected ? (replyTo ? "Reply to message..." : "Type a message...") : "Connecting..."}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={!isConnected || sending || uploading}
            className="message-textarea"
          />
          {showMentionDropdown && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                marginBottom: 8,
                background: '#fff',
                border: '1px solid #e4e7ea',
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                maxHeight: 200,
                overflowY: 'auto',
                minWidth: 250,
              }}
            >
              {getMentionableUsers().length > 0 ? (
                getMentionableUsers().map((user: any) => {
                  const firstName = user.first_name || user.firstName || '';
                  const lastName = user.last_name || user.lastName || '';
                  const displayName = `${firstName} ${lastName}`.trim() || user.email || 'Unknown';
                  const isMember = members.some((m: any) => m.id === user.id);
                  
                  return (
                    <div
                      key={user.id}
                      onClick={() => handleMentionSelect(user)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        borderBottom: '1px solid #f0f0f0',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fff';
                      }}
                    >
                      <Avatar size="small" src={user.avatar_url} icon={<UserOutlined />} />
                      <span>{displayName}</span>
                      {!isMember && (
                        <span style={{ fontSize: 11, color: '#999', marginLeft: 'auto' }}>Not in channel</span>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                  No users found
                </div>
              )}
            </div>
          )}
          {slashCommandOpen && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                marginBottom: 8,
                background: '#fff',
                border: '1px solid #e4e7ea',
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: 260,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#666' }}>
                Commands
              </div>
              {SLASH_COMMANDS.map(({ command, label, action }) => (
                <div
                  key={command}
                  onClick={() => action()}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fff';
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{command}</span>
                  <span style={{ fontSize: 12, color: '#666' }}>{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={(!text.trim() && fileList.length === 0) || sending || uploading || !isConnected}
          className="message-send-button"
          size="small"
          loading={uploading}
        />
      </div>

      {/* Invite Modal */}
      <Modal
        title="User Not in Channel"
        open={inviteModalVisible}
        onOk={handleInviteAndSend}
        onCancel={handleSendWithoutInvite}
        okText="Invite & Send"
        cancelText="Send Without Invite"
      >
        <p>
          <strong>{userToInvite ? `${userToInvite.first_name || userToInvite.firstName || ''} ${userToInvite.last_name || userToInvite.lastName || ''}`.trim() || userToInvite.email : 'This user'}</strong> is not a member of this channel.
        </p>
        <p>Would you like to invite them before sending the message?</p>
      </Modal>

      <CreateTicketFromChatModal
        open={createTicketModalOpen}
        onClose={() => setCreateTicketModalOpen(false)}
        channelId={channelId}
        onSuccess={(ticketNumber) => {
          setCreateTicketModalOpen(false);
          if (channelId && ticketNumber) {
            const linkText = `Created ticket #${ticketNumber}`;
            setText(linkText);
          }
        }}
      />
    </div>
  );
};

